import React, {useState} from 'react';
import {
  ActivityIndicator,
  Dimensions,
  Platform,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import ReactNativeModal from 'react-native-modal';
import Animated, {FadeIn} from 'react-native-reanimated';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import Toast from 'react-native-toast-message';
import {Card, Icon} from '@rneui/themed';
import {observer} from 'mobx-react-lite';

import PaymentMethodComponent from '../../../components/payments/till/PaymentMethod';
import ZenGiftCardsManager from '../../../components/payments/zenGiftCards/ZenGiftCardsManager';
import SafeAreaPadding from '../../../components/SafeAreaPadding';
import Button from '../../../components/themed/Button';
import Text from '../../../components/themed/Text';
import ToastBottom, {ERROR_MESSAGE} from '../../../components/ToastBottom';
import ContentView from '../../../components/ui-controls/layout/ContentView';
import PressableWithOpacityFeedback from '../../../components/ui-controls/PressableWithOpacityFeedback';
import config from '../../../config';
import {useWebScreenDimensions} from '../../../hooks/useWebScreenDimensions';
import {AppRoute} from '../../../models/AppRoute';
import {
  InformationContentType,
  RouteNavigatorScreenProps,
} from '../../../models/navigationTypes';
import {useStores} from '../../../stores/use-stores';
import theme from '../../../style/theme';
import {Analytics} from '../../../utils/analytics/analytics';
import {
  EventName,
  EventScreenName,
  EventSourceType,
} from '../../../utils/analytics/events';
import {RewardsTermsAndConditionsPopper} from '../../rewards/TermsAndConditions';
import OrderTotals from '../components/OrderTotals';
import PickupDeliveryDetails from '../components/PickupDeliveryDetails';
import ThreeDsMobileBrowserDataIframe from '../components/ThreeDsMobileBrowserDataIframe';
import useFetchMobileWebView3dsBrowserDataJSFocusEffect from '../useFetchMobileWebView3dsBrowserDataJSFocusEffect';
import {useRedirectEmptyCart} from '../util/validation';

const CheckoutPaymentScreen = observer(
  ({
    navigation,
  }: RouteNavigatorScreenProps<AppRoute.CART_ORDER_CHECKOUT_PAYMENT>) => {
    // redirect empty cart
    useRedirectEmptyCart(navigation);

    const {cartStore, paymentStore} = useStores();
    const {isWebDesktopScreen} = useWebScreenDimensions();

    const {mobileWebView3DsBrowserDataJS, browserData, setBrowserData} =
      useFetchMobileWebView3dsBrowserDataJSFocusEffect();

    // const [isSubmitting, setSubmitting] = useState(false);
    const [isConfirmOrderModalVisible, setIsConfirmOrderModalVisible] =
      useState(false);

    const getPlaceOrderValidationError = () => {
      if (!cartStore.currentOrder?.order_lines) {
        return 'Cannot place an empty order';
      }

      if (!cartStore.selectedSlot) {
        return 'To place your order please select a delivery or pick up time.';
      }

      if (
        cartStore.hasNominatedPerson &&
        !cartStore.nominatedPersonName.length
      ) {
        return 'A person (18+) must be nominated to accept the delivery';
      }

      // Payment methods and gift cards
      const isGiftCardsBeingUsed = cartStore.isGiftCardsBeingUsed();
      const isGiftCardsNotCoveringTotal =
        cartStore.getTotalChargedOnGiftCardsCents() < cartStore.totalAmount;
      const isCreditCardAvailable = paymentStore.tillUserPaymentInstrument;
      if (
        isGiftCardsBeingUsed &&
        isGiftCardsNotCoveringTotal &&
        !isCreditCardAvailable
      ) {
        return "The gift cards do not cover the total amount and there's no other payment method available. Please add a debit/credit card";
      }

      if (!isGiftCardsBeingUsed && !paymentStore.tillUserPaymentInstrument) {
        return 'Cannot place an order without a valid payment method';
      }

      return null;
    };

    const handlePlaceOrder = () => {
      const error = getPlaceOrderValidationError();

      if (error) {
        console.log('☠️', error);
        Toast.show({
          type: ERROR_MESSAGE,
          text1: error,
        });
      } else {
        setIsConfirmOrderModalVisible(true);
      }
    };

    const submitOrder = () => {
      cartStore.checkoutOrder(navigation, browserData);
      Analytics.logEvent({
        eventName: EventName.SubmitOrder,
        parameters: {
          screen_name: EventScreenName.Checkout,
          source_type: EventSourceType.Button,
          value: cartStore.currentOrder?.total?.cents_inc_tax / 100,
          tax: cartStore.currentOrder?.total?.cents_tax / 100,
          currency: 'AUD',
        },
      });

      // timeout after 1 minute
      setTimeout(() => {
        setIsConfirmOrderModalVisible(false);
      }, 60000);
    };

    const isGiftCardsBeingUsed = cartStore.isGiftCardsBeingUsed();

    if (Platform.OS !== 'web' && !mobileWebView3DsBrowserDataJS) {
      return (
        <ContentView testID="checkout-screen">
          <ActivityIndicator />
        </ContentView>
      );
    }

    return (
      <ContentView testID="checkout-screen">
        <KeyboardAwareScrollView
          style={{backgroundColor: 'white'}}
          keyboardShouldPersistTaps="always"
          showsVerticalScrollIndicator={isWebDesktopScreen}
          showsHorizontalScrollIndicator={isWebDesktopScreen}
          contentContainerStyle={{gap: 16}}>
          <>
            <Animated.View entering={FadeIn}>
              <PaymentMethodComponent
                navigationTarget={AppRoute.CART_ORDER_CHECKOUT_PAYMENT}
              />
            </Animated.View>
            {!!config.FF_IS_GIFT_CARDS_ENABLED && <ZenGiftCardsManager />}
            {!!config.FF_IS_GIFT_CARDS_ENABLED && !!isGiftCardsBeingUsed && (
              <Card noMargin variant="information">
                <Text color="grey2" variant="body2">
                  Any amount that is not covered by the gift card will be
                  automatically charged to your nominated debit/credit card
                </Text>
              </Card>
            )}
            <PickupDeliveryDetails />
            <OrderTotals>
              <Button
                title="PAY NOW"
                onPress={handlePlaceOrder}
                disabled={cartStore.submittingOrder}
                loading={cartStore.submittingOrder}
                testID="checkout-payment-screen-place-order-button"
              />
            </OrderTotals>
          </>
        </KeyboardAwareScrollView>
        <ConfirmOrderPlacementModal
          setIsConfirmOrderModalVisible={setIsConfirmOrderModalVisible}
          isConfirmOrderModalVisible={isConfirmOrderModalVisible}
          submitOrder={submitOrder}
          isSubmitting={cartStore.submittingOrder}
        />
        <ThreeDsMobileBrowserDataIframe
          mobileWebView3DsBrowserDataJS={mobileWebView3DsBrowserDataJS}
          onBrowserData={setBrowserData}
        />
        <SafeAreaPadding bottom />
      </ContentView>
    );
  },
);

const ConfirmOrderPlacementModal = ({
  setIsConfirmOrderModalVisible,
  isConfirmOrderModalVisible,
  submitOrder,
  isSubmitting,
}: any) => {
  const {cartStore} = useStores();

  const insets = useSafeAreaInsets();

  return (
    <ReactNativeModal
      isVisible={isConfirmOrderModalVisible}
      hasBackdrop
      backdropOpacity={0.75}
      onBackdropPress={() => setIsConfirmOrderModalVisible(false)}
      onBackButtonPress={() => setIsConfirmOrderModalVisible(false)}
      style={{padding: 0, margin: 0}}>
      <View
        style={{
          paddingTop: insets.top,
          paddingBottom: insets.bottom,
          height: Dimensions.get('window').height,
          backgroundColor: 'white',
        }}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingHorizontal: 20,
          }}>
          <Text style={{marginTop: 24, marginBottom: 18}} variant="heading1">
            PLACE ORDER
          </Text>
          <PressableWithOpacityFeedback
            onPress={() => setIsConfirmOrderModalVisible(false)}>
            <Icon name="close" size={30} />
          </PressableWithOpacityFeedback>
        </View>
        <ScrollView style={{paddingHorizontal: 20}}>
          {!!cartStore.isDelivery && (
            <>
              <Text variant="body2" color="black" style={styles.confirmText}>
                The registered customer or other adult nominated to accept the
                delivery must present a valid form of ID to the delivery person,
                which will be securely scanned through the delivery
                person&apos;s app to verify that you are aged 18 or over.
              </Text>
              <Text variant="body2" color="black" style={styles.confirmText}>
                Delivery will be refused if ID is not shown.
              </Text>
              <Text variant="body2" color="black" style={styles.confirmText}>
                By placing this order, you confirm that you are over 18 years
                old and that you agree to our{' '}
                <RewardsTermsAndConditionsPopper
                  contentType={InformationContentType.TERMS_AND_CONDITIONS}
                />
                .
              </Text>
            </>
          )}
          {!!cartStore.isPickup && (
            <>
              <Text variant="body2" color="black" style={styles.confirmText}>
                The registered customer (18+) who placed the order must have a
                valid form ID and the credit card used to pay for this order
                available to show at pick up.
              </Text>
              <Text variant="body2" color="black" style={styles.confirmText}>
                Pick up may be refused if any of these items are not shown.
              </Text>
            </>
          )}
        </ScrollView>
        <View style={styles.actions}>
          <Button
            loading={isSubmitting}
            disabled={isSubmitting}
            onPress={submitOrder}>
            CONFIRM & SUBMIT
          </Button>
          <Button
            variant="outlined"
            onPress={() => setIsConfirmOrderModalVisible(false)}>
            BACK
          </Button>
        </View>
      </View>
      <ToastBottom />
    </ReactNativeModal>
  );
};

export default CheckoutPaymentScreen;

const styles = StyleSheet.create({
  webDesktopHorizontalContent: {
    flexDirection: 'row',
    gap: 16,
  },
  webDesktopSectionTitle: {
    color: theme.colour.text.heading.primary,
    fontSize: 16,
    fontWeight: 'bold',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    minHeight: 22,
    backgroundColor: 'white',
  },
  totalText: {
    textAlign: 'right',
    color: theme.colour.text.heading.primary,
    fontWeight: '500',
  },
  totalName: {
    flex: 3,
  },
  totalAmount: {
    flex: 1,
  },
  activityHolder: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  cartItemSpacing: {
    marginBottom: 8,
  },
  bfButton: {
    marginVertical: 16,
  },
  label: {
    color: 'grey',
    fontSize: 12,
    marginBottom: 4,
  },
  section: {
    marginTop: 8,
  },
  confirmText: {
    marginVertical: 8,
  },
  actions: {
    gap: 8,
    flexDirection: 'column',
    padding: 20,
  },
});
